// src/hooks/useDepartments.ts — shared department-list loader.
//
// Single source of truth for the canonical department list, read from
// GET /api/access/departments (DB-backed: filtered server-side to
// is_active = 1, ordered by sort_order then name).
//
// Why this exists (issue #15): two admin surfaces consume departments and
// used to read them from different places —
//   * AccessUsersPage (the filter pill row) fetched the API, so a
//     department added via "+ New Department" appeared immediately.
//   * AccessUserEditPage (the user's Departments multi-select) rendered a
//     hardcoded module constant and never called the API, so new
//     departments never showed up and the const had silently drifted from
//     dbo.departments.
// Both now consume this hook, so the two can't drift again.
//
// Both consuming pages are SSO-admin gated (client-side and via the
// server's requireSsoAdmin), so hitting the admin-only endpoint on mount
// is safe. A caller without access gets a 403, which we swallow into an
// empty list so the UI degrades (no pills) instead of hanging on "loading".

import { useCallback, useEffect, useState } from "react";
import { apiFetch } from "../api";

// Mirrors a row from GET /api/access/departments.
export interface DeptRow {
  id: number;
  name: string;
  sort_order: number;
}

interface DeptListResponse {
  departments: DeptRow[];
}

export interface UseDepartments {
  /** Loaded rows, or null until the first fetch resolves (the "loading"
   *  signal callers can check directly). Empty array means loaded-but-none
   *  or a swallowed fetch error. */
  departments: DeptRow[] | null;
  /** True while a fetch is in flight, including the initial load. */
  loading: boolean;
  /** Re-fetch the list — e.g. after the New Department modal POSTs. */
  reload: () => Promise<void>;
}

export function useDepartments(): UseDepartments {
  const [departments, setDepartments] = useState<DeptRow[] | null>(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch<DeptListResponse>("/api/access/departments");
      setDepartments(res.departments);
    } catch {
      // Non-blocking UI affordance — fall back to an empty list rather than
      // leaving callers stuck on the loading state.
      setDepartments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { departments, loading, reload };
}
